/*
 * Copies each contact-form submission to the team's Google Sheet.
 *
 * The copy is only sent once Shopify has confirmed the submission. Sending it
 * at submit time looked simpler, but a submission Shopify then rejected — bot
 * protection, a server-side validation error — still landed in the sheet, so
 * the team could see an enquiry nobody ever received an e-mail about.
 *
 * The submission is therefore parked in sessionStorage, the page posts to
 * Shopify and comes back, and the copy goes out only when the reloaded page
 * shows a success message ([data-sheets-success], rendered by the sections
 * under form.posted_successfully?) or carries Shopify's contact_posted flag.
 * A parked submission that never gets confirmed expires with the tab.
 *
 * Active only on forms that carry a non-empty data-sheets-endpoint (set in
 * Theme settings → Formularze — arkusz Google).
 */
(function () {
  if (window.__formToSheetsReady) return;
  window.__formToSheetsReady = true;

  // Shopify field name inside contact[…] → sheet column key.
  var FIELD_MAP = {
    'Imię': 'first_name',
    'Nazwisko': 'last_name',
    'Nazwa firmy': 'company',
    'email': 'email',
    'Telefon': 'phone',
    'Usługa': 'service',
    'Data wydarzenia': 'event_date',
    'Liczba porcji': 'portions',
    'Adres przyjęcia': 'address',
    'Godzina dostawy': 'delivery_time',
    'Jaka usługa Cię interesuje': 'service',
    'body': 'message',
    'Wiadomość': 'message',
    'Dodatkowe informacje': 'message',
    'Alergie / nietolerancje pokarmowe / dodatkowe informacje': 'message',
    'Nr zgłoszenia': 'reference',
    // Cake configurator. Mapped explicitly so the sheet's column keys stay
    // stable even if a field's visible label changes.
    'Ilość porcji': 'portions',
    'Rodzaj tortu': 'cake_type',
    'Smak': 'flavor',
    'Kolor tortu': 'color',
    'Sposób projektu': 'design_mode',
    'Wybrany wzór': 'pattern',
    'Ozdoby': 'addons',
    'Opakowanie Bento': 'bento',
    'Zdjęcie inspiracji': 'inspiration_photo',
    'Data odbioru': 'pickup_date',
    'Godzina odbioru': 'pickup_time',
  };

  /*
   * Submission reference, e.g. ZG-20261005-K7Q3.
   *
   * The ZG- prefix is deliberate: Shopify's own order numbers start with OP-,
   * and when both used it nobody could tell an enquiry number from an order
   * number. Keep this in sync with makeReference() in
   * custom-cake-configurator.js — the two must never disagree.
   *
   * It travels in a hidden contact[...] field, so the same number reaches the
   * sheet row and the e-mail Shopify sends, and the cake configurator copies it
   * onto the cart line — which puts it on the Shopify order too. That is what
   * lets the team pair a sheet row with a real order instead of guessing from
   * the name and the pickup date.
   *
   * Filled on page load rather than on submit: the configurator's own submit
   * handler reads the field when building the cart line, and the two scripts
   * must not depend on which one bound its listener first.
   */
  function reference() {
    var now = new Date();
    var day =
      String(now.getFullYear()) +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0');
    var tail = '';
    while (tail.length < 4) tail += Math.random().toString(36).slice(2).toUpperCase();
    return 'ZG-' + day + '-' + tail.replace(/[^A-Z0-9]/g, '').slice(0, 4);
  }

  function collect(form) {
    var params = new URLSearchParams();
    params.append('form_name', form.getAttribute('data-sheets-form') || 'Formularz');
    params.append('page', window.location.pathname);

    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name || el.disabled) return;

      if (el.name === 'sheets_hp') {
        params.append('website', el.value);
        return;
      }

      var match = el.name.match(/^contact\[(.+?)\](\[\])?$/);
      if (!match || match[1] === 'Formularz') return;

      if (el.type === 'file') {
        // Files cannot travel in a beacon; the photo stays attached to the e-mail.
        if (el.files && el.files.length) params.append(match[1], 'dołączone do maila z Shopify');
        return;
      }
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      if (String(el.value).trim() === '') return;

      params.append(FIELD_MAP[match[1]] || match[1], el.value);
    });

    return params;
  }

  /**
   * The body has to go out as URLSearchParams, never as a plain string.
   *
   * sendBeacon picks the content type from what it is handed: URLSearchParams
   * becomes application/x-www-form-urlencoded, a string becomes text/plain.
   * Apps Script only fills e.parameters for form-encoded bodies, so a string
   * arrived with no fields at all, buildRow found no form_name and returned
   * null — the request succeeded and the row was silently dropped. Parking a
   * submission stores it as a string, which is how the two ever met.
   */
  function send(endpoint, params) {
    var body = typeof params === 'string' ? new URLSearchParams(params) : params;
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(endpoint, body)) return;
    } catch (error) {}
    try {
      fetch(endpoint, { method: 'POST', mode: 'no-cors', keepalive: true, body: body });
    } catch (error) {}
  }

  var STORAGE_KEY = 'formToSheets:pending';
  // Long enough to survive the round trip to Shopify and back, short enough
  // that a submission abandoned earlier in the session is never sent later.
  var MAX_AGE = 10 * 60 * 1000;

  function park(endpoint, params) {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ endpoint: endpoint, body: params.toString(), at: Date.now() })
      );
    } catch (error) {
      // Private mode, blocked storage: fall back to sending straight away
      // rather than losing the enquiry altogether.
      send(endpoint, params);
    }
  }

  function confirmed() {
    if (document.querySelector('[data-sheets-success]')) return true;
    return window.location.search.indexOf('contact_posted=true') !== -1;
  }

  function flush() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      return;
    }
    if (!raw) return;

    var parked;
    try {
      parked = JSON.parse(raw);
    } catch (error) {
      return;
    }
    if (!parked || !parked.endpoint || !parked.body) return;
    if (Date.now() - (parked.at || 0) > MAX_AGE) return;

    send(parked.endpoint, parked.body);
  }

  function bind(form) {
    if (form.__sheetsBound) return;
    form.__sheetsBound = true;

    var ref = form.querySelector('[data-sheets-ref]');
    if (ref && !ref.value) ref.value = reference();

    // "submit" only fires once the browser's own validation has passed, so
    // incomplete forms never reach the sheet.
    //
    // Sent here and now rather than held back until Shopify redirects the
    // visitor to a success page. Waiting looked tidier — only confirmed
    // enquiries would reach the sheet — but it made the copy depend on the
    // shape of the page Shopify happens to return, and when a bot challenge
    // or a redirect without the success flag came back instead, every enquiry
    // was lost in silence. A rare row for a submission Shopify later rejected
    // is a far smaller problem than a list that quietly misses enquiries.
    // sendBeacon is built for exactly this: it survives the page unloading.
    form.addEventListener('submit', function () {
      var endpoint = form.getAttribute('data-sheets-endpoint');
      if (!endpoint) return;
      send(endpoint, collect(form));
    });
  }

  function init() {
    document.querySelectorAll('form[data-sheets-endpoint]').forEach(bind);
    // Anything parked by the previous version still goes out, once.
    if (confirmed()) flush();
  }

  init();
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
